import React, { useCallback, useEffect, useRef, useState } from 'react';
import metaConnectionService from '../../services/api/metaConnectionService';
import {
  MetaCampaignBundleDraft,
  MetaChangeRequest,
  MetaObjectType,
  MetaResumePreview,
} from '../../types/metaConnection';
import { MetaLang, MW_STRINGS, reviewSafeText } from '../../i18n/metaReviewLang';
import { useConfirm } from '../tasky/modal';
import { TaskyButton } from '../tasky/button/TaskyButton';

// ============================================================
// Tailwind class constants (旧 CSS-in-JS 定義から移行)
// matches the existing MetaDashboardPage look
// ============================================================

const SECTION = 'bg-white border border-gray-200 rounded-xl p-5 mt-6';

const SECTION_TITLE = 'text-[16px] font-bold text-gray-800 mt-0 mx-0 mb-2';

const INTRO = 'mt-0 mx-0 mb-4 text-[13px] text-gray-500 leading-[1.7]';

const GRID = 'grid grid-cols-2 gap-3 max-[640px]:grid-cols-[1fr]';

const FIELD = 'flex flex-col gap-1.5';

const FIELD_LABEL = 'text-[12px] font-semibold text-gray-700';

const INPUT =
  'px-3 py-2.5 border border-gray-300 rounded-lg text-[14px] text-gray-800 focus:outline-none focus:border-[#1877f2] focus:shadow-[0_0_0_3px_rgba(24,119,242,0.12)]';

const HINT = 'text-[11px] text-gray-400';

const PRIMARY_BUTTON =
  'px-5 py-2.5 border-0 rounded-lg bg-[#1877f2] text-white text-[14px] font-semibold cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed';

const SECONDARY_BUTTON =
  'px-[18px] py-2.5 border border-gray-300 rounded-lg bg-white text-gray-700 text-[14px] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed';

const BUTTON_ROW = 'flex flex-wrap gap-2 mt-4';

const CONFIRM_PANEL = 'mt-4 border border-blue-200 bg-[#f8fbff] rounded-[10px] p-4';

const CONFIRM_GRID =
  'grid grid-cols-[200px_1fr] gap-x-3 gap-y-2 text-[13px] max-[640px]:grid-cols-[1fr] max-[640px]:gap-y-1';

const CONFIRM_LABEL = 'text-slate-500';

const CONFIRM_VALUE = 'text-gray-800 font-medium break-all';

const RESULT_PANEL_BASE = 'mt-4 rounded-[10px] p-[14px] text-[13px] leading-[1.7] border';
const RESULT_PANEL_OK = `${RESULT_PANEL_BASE} border-green-200 bg-green-50 text-green-700`;
const RESULT_PANEL_WARN = `${RESULT_PANEL_BASE} border-orange-200 bg-orange-50 text-orange-800`;
const RESULT_PANEL_ERROR = `${RESULT_PANEL_BASE} border-red-200 bg-red-50 text-red-700`;

const OBJECT_LIST =
  "mt-1.5 mx-0 mb-0 pl-[18px] font-['SFMono-Regular',Menlo,monospace] text-[12px] text-gray-800";

const ALERT_BASE = 'mt-3 px-3 py-2.5 rounded-lg text-[13px] border';
const ALERT_ERROR = `${ALERT_BASE} text-red-700 bg-red-50 border-red-200`;
const ALERT_SUCCESS = `${ALERT_BASE} text-green-700 bg-green-50 border-green-200`;

const TABLE = 'w-full border-collapse text-[12px] mt-3';

const TH = 'text-left p-2 text-gray-500 font-semibold border-b border-b-gray-200 whitespace-nowrap';

const TD = 'p-2 border-b border-b-gray-100 text-gray-800 align-top';

const TABLE_SCROLL = 'overflow-x-auto';

const EMPTY = 'p-5 text-center text-gray-400 text-[13px]';

// ============================================================
// Helpers
// ============================================================

function errorMessage(err: unknown, fallback: string): string {
  const anyErr = err as { response?: { data?: { message?: string } } };
  return anyErr?.response?.data?.message || fallback;
}

function englishErrorMessage(err: unknown, fallback: string): string {
  const typed = err as {
    code?: string;
    response?: { status?: number; data?: { error_code?: string; code?: string; error?: { code?: string } } };
  };
  const code = typed.response?.data?.error_code
    || typed.response?.data?.code
    || typed.response?.data?.error?.code
    || typed.code
    || typed.response?.status
    || 'unknown';
  return `${fallback} (Error: ${code})`;
}

const OBJECT_ORDER: MetaObjectType[] = ['campaign', 'adset', 'adcreative', 'ad'];

function newIdempotencyKey(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `tasky-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

interface Props {
  /** ad_accounts.id of the connected Meta account to write to. */
  accountId: number | null;
  /** False when Meta did not grant ads_management on the stored token. */
  canManageAds: boolean;
  /**
   * サーバ側の書き込みゲートを通した結果。許可リスト漏れが最も踏みやすい
   * ので、送信して 403 を食う前にここで止める。
   */
  writeAllowed: boolean;
  lang: MetaLang;
}

// ============================================================
// Component
// ============================================================

const MetaAdsWriteSection: React.FC<Props> = ({
  accountId, canManageAds, writeAllowed, lang,
}) => {
  const t = MW_STRINGS[lang];
  const confirm = useConfirm();

  const [campaignName, setCampaignName] = useState('Review Campaign');
  const [objective, setObjective] = useState('OUTCOME_TRAFFIC');
  const [adSetName, setAdSetName] = useState('Review Ad Set');
  const [dailyBudget, setDailyBudget] = useState('100000');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [country, setCountry] = useState('JP');
  const [creativeName, setCreativeName] = useState('Review Creative');
  const [pageId, setPageId] = useState('');
  const [link, setLink] = useState('https://tasky-ai.com/');
  const [primaryText, setPrimaryText] = useState('Plan, generate and manage ads with Tasky.');
  const [headline, setHeadline] = useState('Tasky');
  const [adName, setAdName] = useState('Review Ad');

  const [preview, setPreview] = useState<MetaChangeRequest | null>(null);
  const [result, setResult] = useState<MetaChangeRequest | null>(null);
  const [audit, setAudit] = useState<MetaChangeRequest[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [resumePreview, setResumePreview] = useState<{ change: MetaChangeRequest; plan: MetaResumePreview } | null>(null);

  // A new idempotency key is minted only when the plan actually changes, so a
  // double-click or a retry after a timeout reuses the same key.
  const idempotencyRef = useRef<{ fingerprint: string; key: string } | null>(null);

  const loadAudit = useCallback(async () => {
    try {
      setAudit(await metaConnectionService.getAudit());
    } catch (err) {
      setError(lang === 'en' ? englishErrorMessage(err, t.errAudit) : errorMessage(err, t.errAudit));
    }
  }, [lang, t.errAudit]);

  useEffect(() => {
    loadAudit();
  }, [loadAudit]);

  const buildPreview = async () => {
    if (accountId === null) return;
    setSubmitting(true);
    setError(null);
    setNotice(null);
    setResult(null);
    try {
      const draft: MetaCampaignBundleDraft = {
        account_id: accountId,
        operation_type: 'campaign_bundle_create',
        campaign_name: campaignName.trim(),
        objective: objective.trim(),
        special_ad_categories: [],
        ad_set_name: adSetName.trim(),
        daily_budget: Number(dailyBudget),
        billing_event: 'IMPRESSIONS',
        optimization_goal: 'LINK_CLICKS',
        targeting: { geo_locations: { countries: [country.trim().toUpperCase()] } },
        ...(startTime ? { start_time: startTime } : {}),
        ...(endTime ? { end_time: endTime } : {}),
        creative_name: creativeName.trim(),
        page_id: pageId.trim(),
        link: link.trim(),
        message: primaryText.trim(),
        headline: headline.trim(),
        ad_name: adName.trim(),
      };

      const fingerprint = JSON.stringify(draft);
      if (idempotencyRef.current?.fingerprint !== fingerprint) {
        idempotencyRef.current = { fingerprint, key: newIdempotencyKey() };
      }

      const change = await metaConnectionService.createChange({
        ...draft,
        idempotency_key: idempotencyRef.current.key,
      });
      setPreview(change);
      setNotice(t.noticePreviewSaved);
    } catch (err) {
      setError(lang === 'en' ? englishErrorMessage(err, t.errPreview) : errorMessage(err, t.errPreview));
    } finally {
      setSubmitting(false);
    }
  };

  const validatePreview = async () => {
    if (!preview) return;
    setSubmitting(true);
    setError(null);
    try {
      await metaConnectionService.validateChange(preview.id);
      setNotice(t.noticeValidated);
    } catch (err) {
      setError(lang === 'en' ? englishErrorMessage(err, t.errValidate) : errorMessage(err, t.errValidate));
    } finally {
      setSubmitting(false);
    }
  };

  const executePreview = async () => {
    if (!preview) return;
    // Explicit user confirmation is required before anything reaches Meta.
    if (!(await confirm({ title: t.confirmDialog(preview.account.account_id_suffix) }))) return;

    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const executed = await metaConnectionService.executeChange(preview.id);
      setResult(executed);
      setPreview(null);
    } catch (err) {
      // A partial failure comes back as an error but still carries the ids of
      // whatever was created, so surface it instead of dropping it.
      const partial = (err as { response?: { data?: { data?: MetaChangeRequest } } })?.response?.data
        ?.data;
      if (partial) {
        setResult(partial);
        setPreview(null);
      }
      setError(lang === 'en' ? englishErrorMessage(err, t.errExecute) : errorMessage(err, t.errExecute));
    } finally {
      setSubmitting(false);
      loadAudit();
    }
  };

  const inspectResume = async (change: MetaChangeRequest) => {
    setSubmitting(true);
    setError(null);
    setResumePreview(null);
    try {
      const plan = await metaConnectionService.getResumePreview(change.id);
      setResumePreview({ change, plan });
    } catch (err) {
      const fallback = lang === 'ja' ? 'この変更は安全に再開できません。' : 'This change cannot be safely resumed.';
      setError(lang === 'en' ? englishErrorMessage(err, fallback) : errorMessage(err, fallback));
    } finally {
      setSubmitting(false);
    }
  };

  const resumeBundle = async () => {
    if (!resumePreview) return;
    const { change, plan } = resumePreview;
    const message = lang === 'ja'
      ? `作成済みの ${Object.keys(plan.created).length} 件を再利用し、${plan.next_step} から残りを PAUSED 状態で作成します。続行しますか？`
      : `Reuse ${Object.keys(plan.created).length} existing objects and create the remaining objects from ${plan.next_step} as PAUSED?`;
    if (!(await confirm({ title: message }))) return;
    setSubmitting(true);
    setError(null);
    try {
      const resumed = await metaConnectionService.resumeChange(change.id, plan.revision);
      setResult(resumed);
      setResumePreview(null);
      await loadAudit();
    } catch (err) {
      setResumePreview(null);
      const fallback = lang === 'ja' ? '再開に失敗しました。監査ログを確認してください。' : 'Resume failed. Check the audit log.';
      setError(lang === 'en' ? englishErrorMessage(err, fallback) : errorMessage(err, fallback));
      await loadAudit();
    } finally {
      setSubmitting(false);
    }
  };

  if (!canManageAds) {
    return (
      <div className={SECTION}>
        <h2 className={SECTION_TITLE}>{t.sectionTitle}</h2>
        <div className={ALERT_ERROR}>{t.disabledNoWrite}</div>
      </div>
    );
  }

  // ads_management はあるが環境側で許可されていない場合。理由を分けないと
  // 「再認可してください」と言われて何度やっても直らない袋小路になる。
  if (!writeAllowed) {
    return (
      <div className={SECTION}>
        <h2 className={SECTION_TITLE}>{t.sectionTitle}</h2>
        <div className={ALERT_ERROR}>{t.disabledNotAllowlisted}</div>
      </div>
    );
  }

  const createdIds = result ? Object.entries(result.object_ids) : [];
  const missing = result
    ? OBJECT_ORDER.filter((type) => !(type in (result.object_ids || {})))
    : [];
  const resultTone =
    result?.status === 'succeeded' ? 'ok' : result?.status === 'partial_failure' ? 'warn' : 'error';

  return (
    <>
      <div className={SECTION}>
        <h2 className={SECTION_TITLE}>{t.sectionTitle}</h2>
        <p className={INTRO}>{t.sectionIntro}</p>

        {!preview && (
          <>
            <div className={GRID}>
              <div className={FIELD}>
                <label className={FIELD_LABEL} htmlFor="meta-campaign-name">{t.campaignName}</label>
                <input className={INPUT} id="meta-campaign-name" value={campaignName} onChange={(e) => setCampaignName(e.target.value)} />
              </div>
              <div className={FIELD}>
                <label className={FIELD_LABEL} htmlFor="meta-objective">{t.objective}</label>
                <input className={INPUT} id="meta-objective" value={objective} onChange={(e) => setObjective(e.target.value)} />
              </div>
              <div className={FIELD}>
                <label className={FIELD_LABEL} htmlFor="meta-adset-name">{t.adSetName}</label>
                <input className={INPUT} id="meta-adset-name" value={adSetName} onChange={(e) => setAdSetName(e.target.value)} />
              </div>
              <div className={FIELD}>
                <label className={FIELD_LABEL} htmlFor="meta-daily-budget">{t.dailyBudget}</label>
                <input
                  className={INPUT}
                  id="meta-daily-budget"
                  type="number"
                  value={dailyBudget}
                  onChange={(e) => setDailyBudget(e.target.value)}
                />
                <span className={HINT}>{t.dailyBudgetHint}</span>
              </div>
              <div className={FIELD}>
                <label className={FIELD_LABEL} htmlFor="meta-start-time">{t.startTime}</label>
                <input
                  className={INPUT}
                  id="meta-start-time"
                  type="datetime-local"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
              <div className={FIELD}>
                <label className={FIELD_LABEL} htmlFor="meta-end-time">{t.endTime}</label>
                <input
                  className={INPUT}
                  id="meta-end-time"
                  type="datetime-local"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
              <div className={FIELD}>
                <label className={FIELD_LABEL} htmlFor="meta-country">{t.country}</label>
                <input className={INPUT} id="meta-country" value={country} onChange={(e) => setCountry(e.target.value)} />
              </div>
              <div className={FIELD}>
                <label className={FIELD_LABEL} htmlFor="meta-creative-name">{t.creativeName}</label>
                <input className={INPUT} id="meta-creative-name" value={creativeName} onChange={(e) => setCreativeName(e.target.value)} />
              </div>
              <div className={FIELD}>
                <label className={FIELD_LABEL} htmlFor="meta-page-id">{t.pageId}</label>
                <input className={INPUT} id="meta-page-id" value={pageId} onChange={(e) => setPageId(e.target.value)} />
              </div>
              <div className={FIELD}>
                <label className={FIELD_LABEL} htmlFor="meta-link">{t.link}</label>
                <input className={INPUT} id="meta-link" value={link} onChange={(e) => setLink(e.target.value)} />
              </div>
              <div className={FIELD}>
                <label className={FIELD_LABEL} htmlFor="meta-primary-text">{t.primaryText}</label>
                <input className={INPUT} id="meta-primary-text" value={primaryText} onChange={(e) => setPrimaryText(e.target.value)} />
              </div>
              <div className={FIELD}>
                <label className={FIELD_LABEL} htmlFor="meta-headline">{t.headline}</label>
                <input className={INPUT} id="meta-headline" value={headline} onChange={(e) => setHeadline(e.target.value)} />
              </div>
              <div className={FIELD}>
                <label className={FIELD_LABEL} htmlFor="meta-ad-name">{t.adName}</label>
                <input className={INPUT} id="meta-ad-name" value={adName} onChange={(e) => setAdName(e.target.value)} />
              </div>
            </div>

            <div className={BUTTON_ROW}>
              <button className={PRIMARY_BUTTON} onClick={buildPreview} disabled={submitting || accountId === null}>
                {submitting ? t.building : t.buildPreview}
              </button>
            </div>
          </>
        )}

        {preview && (
          <div className={CONFIRM_PANEL}>
            <h2 className={SECTION_TITLE}>{t.confirmTitle}</h2>
            <p className={INTRO}>{t.confirmIntro}</p>
            <div className={CONFIRM_GRID}>
              <div className={CONFIRM_LABEL}>{t.cAccount}</div>
              <div className={CONFIRM_VALUE}>
                {reviewSafeText(preview.account.account_name, lang, 'Meta ad account')}
              </div>

              <div className={CONFIRM_LABEL}>{t.cAccountId}</div>
              <div className={CONFIRM_VALUE}>{preview.account.account_id_suffix}</div>

              <div className={CONFIRM_LABEL}>{t.cOperation}</div>
              <div className={CONFIRM_VALUE}>
                {preview.operation_type === 'campaign_bundle_create'
                  ? t.opBundleCreate
                  : preview.operation_type === 'object_update'
                    ? t.opUpdate
                    : t.opPause}
              </div>

              <div className={CONFIRM_LABEL}>{t.cTargets}</div>
              <div className={CONFIRM_VALUE}>Campaign / Ad Set / Ad Creative / Ad</div>

              <div className={CONFIRM_LABEL}>{t.cBefore}</div>
              <div className={CONFIRM_VALUE}>
                {preview.before ? JSON.stringify(preview.before) : t.notSet}
              </div>

              <div className={CONFIRM_LABEL}>{t.cAfter}</div>
              <div className={CONFIRM_VALUE}>{JSON.stringify(preview.after)}</div>

              <div className={CONFIRM_LABEL}>{t.cDailyBudget}</div>
              <div className={CONFIRM_VALUE}>{preview.summary.daily_budget ?? t.notSet}</div>

              <div className={CONFIRM_LABEL}>{t.cLifetimeBudget}</div>
              <div className={CONFIRM_VALUE}>{preview.summary.lifetime_budget ?? t.notSet}</div>

              <div className={CONFIRM_LABEL}>{t.cSchedule}</div>
              <div className={CONFIRM_VALUE}>
                {preview.summary.start_time || t.notSet} → {preview.summary.end_time || t.notSet}
              </div>

              <div className={CONFIRM_LABEL}>{t.cResultStatus}</div>
              <div className={CONFIRM_VALUE}>{preview.summary.result_status}</div>
            </div>

            <div className={BUTTON_ROW}>
              <button className={SECONDARY_BUTTON} onClick={validatePreview} disabled={submitting}>
                {submitting ? t.validating : t.validate}
              </button>
              <button className={PRIMARY_BUTTON} onClick={executePreview} disabled={submitting}>
                {submitting ? t.executing : t.execute}
              </button>
              <button className={SECONDARY_BUTTON} onClick={() => setPreview(null)} disabled={submitting}>
                {t.discard}
              </button>
            </div>
          </div>
        )}

        {notice && <div className={ALERT_SUCCESS}>{notice}</div>}
        {error && <div className={ALERT_ERROR}>{error}</div>}

        {result && (
          <div
            className={
              resultTone === 'ok'
                ? RESULT_PANEL_OK
                : resultTone === 'warn'
                  ? RESULT_PANEL_WARN
                  : RESULT_PANEL_ERROR
            }
          >
            <strong>
              {t.resultTitle}:{' '}
              {result.status === 'succeeded'
                ? t.resultSucceeded
                : result.status === 'partial_failure'
                  ? t.resultPartial
                  : t.resultFailed}
            </strong>

            <div style={{ marginTop: 8 }}>
              {t.createdObjects}
              <ul className={OBJECT_LIST}>
                {createdIds.length === 0 ? (
                  <li>-</li>
                ) : (
                  createdIds.map(([type, id]) => (
                    <li key={type}>
                      {type}: {id}
                    </li>
                  ))
                )}
              </ul>
            </div>

            {missing.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {t.notCreated}
                <ul className={OBJECT_LIST}>
                  {missing.map((type) => (
                    <li key={type}>{type}</li>
                  ))}
                </ul>
              </div>
            )}

            <div style={{ marginTop: 8 }}>
              {t.graphCalls}
              <ul className={OBJECT_LIST}>
                {result.graph_requests.map((call, i) => (
                  <li key={`${call.endpoint}-${i}`}>
                    {call.method} {call.endpoint} — {call.ok ? 'OK' : 'NG'}
                  </li>
                ))}
              </ul>
            </div>

            {result.error_summary && <div style={{ marginTop: 8 }}>{result.error_summary}</div>}
          </div>
        )}
      </div>

      <div className={SECTION}>
        <h2 className={SECTION_TITLE}>{t.auditTitle}</h2>
        {resumePreview && (
          <div className={CONFIRM_PANEL}>
            <h3 className={SECTION_TITLE}>{lang === 'ja' ? '途中再開の確認' : 'Confirm bundle resume'}</h3>
            <p className={INTRO}>{lang === 'ja' ? 'Meta 上の作成済みオブジェクトと PAUSED 状態を確認しました。内容を再確認してから再開してください。' : 'Existing objects and PAUSED status were verified on Meta. Review before resuming.'}</p>
            <div className={CONFIRM_GRID}>
              <div className={CONFIRM_LABEL}>{t.cAccount}</div>
              <div className={CONFIRM_VALUE}>{resumePreview.change.account.account_id_suffix}</div>
              <div className={CONFIRM_LABEL}>{t.createdObjects}</div>
              <div className={CONFIRM_VALUE}>{Object.entries(resumePreview.plan.created).map(([type, id]) => `${type}=${id}`).join(', ')}</div>
              <div className={CONFIRM_LABEL}>{lang === 'ja' ? '次の作成対象' : 'Next object'}</div>
              <div className={CONFIRM_VALUE}>{resumePreview.plan.next_step}</div>
              <div className={CONFIRM_LABEL}>{t.cDailyBudget}</div>
              <div className={CONFIRM_VALUE}>{resumePreview.change.summary.daily_budget ?? t.notSet}</div>
              <div className={CONFIRM_LABEL}>{t.cResultStatus}</div>
              <div className={CONFIRM_VALUE}>PAUSED</div>
            </div>
            <div className={BUTTON_ROW}>
              <TaskyButton className={PRIMARY_BUTTON} onClick={resumeBundle} disabled={submitting}>{lang === 'ja' ? '残りを作成' : 'Create remaining objects'}</TaskyButton>
              <TaskyButton className={SECONDARY_BUTTON} onClick={() => setResumePreview(null)} disabled={submitting}>{t.discard}</TaskyButton>
            </div>
          </div>
        )}
        <div className={BUTTON_ROW} style={{ marginTop: 0 }}>
          <button className={SECONDARY_BUTTON} onClick={loadAudit}>{t.auditRefresh}</button>
        </div>
        {audit.length === 0 ? (
          <div className={EMPTY}>{t.auditEmpty}</div>
        ) : (
          <div className={TABLE_SCROLL}>
            <table className={TABLE}>
              <thead>
                <tr>
                  <th className={TH}>{t.thWhen}</th>
                  <th className={TH}>{t.thOperation}</th>
                  <th className={TH}>{t.thAccount}</th>
                  <th className={TH}>{t.thStatus}</th>
                  <th className={TH}>{t.thObjects}</th>
                  <th className={TH}>{t.thBy}</th>
                  <th className={TH}>{lang === 'ja' ? '操作' : 'Action'}</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((row) => (
                  <tr key={row.id}>
                    <td className={TD}>{row.executed_at || row.created_at || '-'}</td>
                    <td className={TD}>
                      {row.operation_type === 'campaign_bundle_create'
                        ? t.opBundleCreate
                        : row.operation_type === 'object_update'
                          ? t.opUpdate
                          : t.opPause}
                    </td>
                    <td className={TD}>{row.account.account_id_suffix || row.account.meta_ad_account_id || '-'}</td>
                    <td className={TD}>{row.status}</td>
                    <td className={TD}>
                      {Object.entries(row.object_ids)
                        .map(([type, id]) => `${type}=${id}`)
                        .join(', ') || '-'}
                    </td>
                    <td className={TD}>
                      {reviewSafeText(
                        row.approved_by?.name ?? row.requested_by.name,
                        lang,
                        'Review User',
                      )}
                    </td>
                    <td className={TD}>
                      {row.status === 'partial_failure' && row.operation_type === 'campaign_bundle_create' && (
                        <TaskyButton className={SECONDARY_BUTTON} onClick={() => inspectResume(row)} disabled={submitting}>
                          {lang === 'ja' ? '再開可否を確認' : 'Check resume'}
                        </TaskyButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default MetaAdsWriteSection;
